import intl from 'react-intl-universal';
import type { ILocaleTextMap } from '../types/localeText';
import {
  getPreferredRuntimeLocale,
  normalizeRuntimeLocale,
  SupportedRuntimeLocale,
} from './runtimeLocale';

type LocaleSource = string | ILocaleTextMap | null | undefined;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === 'object' && !(value instanceof Array);
};

const pickString = (value: unknown): string => {
  return typeof value === 'string' ? value : '';
};

const hasText = (value: unknown): boolean => {
  return pickString(value).trim().length > 0;
};

export const normalizeLocale = (locale?: string): SupportedRuntimeLocale => {
  return normalizeRuntimeLocale(locale) || 'zh-CN';
};

/**
 * 统一获取当前运行时语言：
 * 1. URL / storage 强制语言
 * 2. react-intl-universal 已初始化的 currentLocale
 * 3. 后端下发的默认 locale
 * 4. 最终回退中文
 */
export const getRuntimeLocale = (): SupportedRuntimeLocale => {
  const forcedLocale = getPreferredRuntimeLocale();
  if (forcedLocale) {
    return forcedLocale;
  }

  try {
    const options = intl.getInitOptions();
    if (options && options.currentLocale) {
      return normalizeLocale(options.currentLocale);
    }
  } catch (error) {
    // ignore
  }

  if (typeof window !== 'undefined' && window.configDetail?.locale) {
    return normalizeLocale(window.configDetail.locale);
  }

  return 'zh-CN';
};

export const normalizeLocaleTextMap = (
  value?: LocaleSource,
  fallbackValue: LocaleSource = ''
): ILocaleTextMap => {
  const map = isPlainObject(value)
    ? value
    : isPlainObject(fallbackValue)
      ? fallbackValue
      : null;

  if (map) {
    return {
      'zh-CN': pickString(map['zh-CN']),
      'en-US': pickString(map['en-US']),
    };
  }

  return {
    'zh-CN': pickString(fallbackValue),
    'en-US': '',
  };
};

/**
 * 文案优先级保持统一：
 * - 同时有中英：按当前 locale 取值
 * - 仅填一个语种：两个环境都复用这一份
 * - 都没有：回退 legacy 单值
 */
export const resolveLocaleText = (
  legacyValue?: LocaleSource,
  localeValue?: LocaleSource,
  locale: string = getRuntimeLocale()
): string => {
  let finalLegacyValue = legacyValue;
  let finalLocaleValue = localeValue;

  if (isPlainObject(finalLegacyValue) && !finalLocaleValue) {
    finalLocaleValue = finalLegacyValue;
    finalLegacyValue = '';
  }

  const map = normalizeLocaleTextMap(finalLocaleValue, finalLegacyValue);
  const zhText = pickString(map['zh-CN']);
  const enText = pickString(map['en-US']);

  if (hasText(zhText) && hasText(enText)) {
    return normalizeLocale(locale) === 'en-US' ? enText : zhText;
  }

  return zhText || enText || pickString(finalLegacyValue);
};

/**
 * 搜索场景会同时混用 legacy 单值、`*_i18n` 和 `language_json` 的 map，
 * 这里统一展开成去重后的候选文本，避免业务组件自己手写 3~4 套取值分支。
 */
export const getSearchableLocaleTextValues = (
  ...sources: LocaleSource[]
): string[] => {
  const values = sources
    .flatMap((source) => {
      if (isPlainObject(source)) {
        const map = normalizeLocaleTextMap(source);
        return [pickString(map['zh-CN']), pickString(map['en-US'])];
      }

      return [pickString(source)];
    })
    .filter(hasText);

  return Array.from(new Set(values));
};